import {
  isArtifactTabId,
  type ArtifactTabId,
} from "@/lib/artifactTabUnlock";

export const WORKBENCH_ARTIFACT_TAB_QUERY_PARAM = "artifactTab";
export const WORKBENCH_FOCUS_TOOL_QUERY_PARAM = "focusTool";

export type WorkbenchPathOptions = {
  focusTool?: string | null;
};

/**
 * Parses the workbench artifact tab query value. Returns null when missing or unknown.
 */
export function parseArtifactTabSearchParam(
  raw: string | null | undefined,
): ArtifactTabId | null {
  if (!raw) {
    return null;
  }
  return isArtifactTabId(raw) ? raw : null;
}

/**
 * Resolves a requested tab against snapshot-driven unlock rules.
 * Before the workflow snapshot loads ({@code unlockMap.overview} is false), honors valid
 * requested ids so deep links can mount the target tab optimistically.
 */
export function resolveArtifactTabForUnlockMap(
  requested: ArtifactTabId | null | undefined,
  unlockMap: Record<ArtifactTabId, boolean>,
): ArtifactTabId {
  const tab = requested ?? "overview";
  if (!isArtifactTabId(tab)) {
    return "overview";
  }
  if (!unlockMap.overview) {
    return tab;
  }
  if (unlockMap[tab]) {
    return tab;
  }
  return "overview";
}

/**
 * Builds the conversation workbench path. Omits overview artifactTab (canonical URL).
 * Optional {@code focusTool} scrolls to an unresolved tool card on mount.
 */
export function buildWorkbenchConversationPath(
  projectId: string,
  conversationId: string,
  tab: ArtifactTabId,
  options?: WorkbenchPathOptions,
): string {
  const base = `/dashboard/projects/${projectId}/conversations/${conversationId}`;
  const params = new URLSearchParams();
  if (tab !== "overview") {
    params.set(WORKBENCH_ARTIFACT_TAB_QUERY_PARAM, tab);
  }
  const focusTool = options?.focusTool?.trim();
  if (focusTool) {
    params.set(WORKBENCH_FOCUS_TOOL_QUERY_PARAM, focusTool);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/** True when the URL tab param reflects a pending user tab change (overview = absent param). */
export function urlTabMatchesPendingTab(
  urlTab: ArtifactTabId | null,
  pending: ArtifactTabId,
): boolean {
  return urlTab === pending || (urlTab == null && pending === "overview");
}
